import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Select, TextField } from '@mui/material';
import { toast } from 'react-hot-toast';
import { dueDateFromVendorTerm, EXTRACTED_DUE_DATE, SIXTY_DAYS_EOM, THIRTY_DAYS_EOM } from './helper';

const CUSTOM = 'custom';
export { EXTRACTED_DUE_DATE, SIXTY_DAYS_EOM, THIRTY_DAYS_EOM };

const fallbackTerms = [
  { Id: THIRTY_DAYS_EOM, Name: '30 Days EOM' },
  { Id: SIXTY_DAYS_EOM, Name: '60 Days EOM' }
];

const inferTerm = (invoiceDate, dueDate, terms) => {
  const matchingTerm = terms.find(
    (candidate) => dueDate && dueDate === dueDateFromVendorTerm(invoiceDate, '', candidate)
  );
  if (matchingTerm) return String(matchingTerm.Id);
  return CUSTOM;
};

const DueDateInput = ({
  invoiceDate,
  value,
  onChange,
  onBlur,
  onKeyDown,
  autoFocus,
  disabled,
  size,
  variant,
  termsOnly,
  terms,
  loading
}) => {
  const availableTerms = Array.isArray(terms) ? terms : fallbackTerms;
  const [term, setTerm] = useState(() =>
    termsOnly ? String(value?.value || value || '') : inferTerm(invoiceDate, value, availableTerms)
  );

  useEffect(() => {
    setTerm(termsOnly ? String(value?.value || value || '') : inferTerm(invoiceDate, value, availableTerms));
  }, [availableTerms, invoiceDate, termsOnly, value]);

  const changeTerm = (event) => {
    const nextTerm = event.target.value;
    setTerm(nextTerm);
    const selectedTerm = availableTerms.find((candidate) => String(candidate.Id) === String(nextTerm));
    if (termsOnly) {
      onChange(selectedTerm || null);
      return;
    }
    if (nextTerm === CUSTOM) return;

    const calculatedDueDate = dueDateFromVendorTerm(invoiceDate, '', selectedTerm);
    if (!calculatedDueDate) {
      setTerm(CUSTOM);
      toast.error('Set the bill date before selecting payment terms');
      return;
    }
    onChange(calculatedDueDate);
  };

  const handleBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    onBlur?.(event);
  };

  return (
    <div onBlur={handleBlur} onKeyDown={onKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Select
        autoFocus={autoFocus}
        disabled={disabled || loading}
        size={size}
        variant={variant}
        value={term}
        onChange={changeTerm}
        displayEmpty={termsOnly}
        renderValue={termsOnly && !term ? () => 'Select default due date' : undefined}
        MenuProps={{ disablePortal: true }}
        fullWidth
      >
        {termsOnly && <MenuItem value={EXTRACTED_DUE_DATE}>Use extracted due date</MenuItem>}
        {availableTerms.map((qboTerm) => (
          <MenuItem key={qboTerm.Id} value={String(qboTerm.Id)}>
            {qboTerm.Name}
          </MenuItem>
        ))}
        {!termsOnly && <MenuItem value={CUSTOM}>Custom</MenuItem>}
      </Select>
      {!termsOnly && term === CUSTOM && (
        <TextField
          disabled={disabled}
          type="date"
          size={size}
          variant={variant}
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          inputProps={{ 'aria-label': 'Custom due date' }}
          fullWidth
        />
      )}
    </div>
  );
};

DueDateInput.propTypes = {
  invoiceDate: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  termsOnly: PropTypes.bool,
  terms: PropTypes.array,
  loading: PropTypes.bool
};

DueDateInput.defaultProps = {
  invoiceDate: '',
  value: '',
  onBlur: undefined,
  onKeyDown: undefined,
  autoFocus: false,
  disabled: false,
  size: 'small',
  variant: 'outlined',
  termsOnly: false,
  terms: null,
  loading: false
};

export default DueDateInput;
